import type {CarSlideType} from '@smart/editorial-components';
import {CarSlide} from '@smart/editorial-components';
import {
  createLegalDisclaimerProps,
  createLinkProps,
} from '@smart/website-aem-utils';
import type {CarSlideContent} from '@smart/website-aem-utils/src/contents/create-car-slide-content';
import * as React from 'react';
import {useFeatureServices} from '../hooks/use-feature-services';
import {PageModelContext} from './page';

export interface CarSlideWrapperProps {
  readonly content: CarSlideContent;
  readonly cdnOrigin: string;
}

export const CarSlideWrapper = React.memo(
  ({content, cdnOrigin}: CarSlideWrapperProps) => {
    const pageModel = React.useContext(PageModelContext);
    const {languageTag = ``, marketId = ``} = pageModel?.locale || {};

    const {endpointDirectory, loginService, historyService, pubSubService} =
      useFeatureServices();

    const props: CarSlideType = {
      removeTopSpacing: content.removeTopSpacing ?? false,
      pngSequenceBaseUrl: new URL(
        `${process.env.PNG_SEQUENCE_BASE_URL!}/car-slide`,
        cdnOrigin,
      ).href,
      models: content.models.map(
        (
          {
            modelName,
            headlineTag,
            modelDescription,
            priceTag,
            leasingPriceTag,
            priceTagAnnotation,
            cta,
          },
          index,
        ) => ({
          title: modelName,
          headlineTag: headlineTag as 'h1' | 'h2' | 'h3' | 'h4' | undefined,
          subTitle: modelDescription,
          priceTag,
          leasingPriceTag,
          priceTagAnnotation,
          link: cta
            ? createLinkProps({
                historyService,
                content: cta,
                pubSubService,
                websiteTrackingData: {
                  type: `navigation`,
                  region: `slider_car`,
                  name: `${cta.label}.${index}`,
                },
                envName: endpointDirectory.envName,
                loginState: loginService?.loginState,
                languageTag,
                marketId,
              })
            : undefined,
        }),
      ),
      legalDisclaimers: createLegalDisclaimerProps(
        content.legalDisclaimers,
        historyService,
      ),
      mode: content.modeSelector,
    };

    return <CarSlide {...props} />;
  },
  (prevProps, nextProps) => prevProps.content.id === nextProps.content.id,
);

CarSlideWrapper.displayName = `CarSlideWrapper`;
